import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  AlertCircle,
  Shield,
  Fingerprint,
  ArrowRight,
  Zap,
  Terminal,
  Loader2,
  Clock,
  XCircle,
} from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { Button, Input, Card } from '../components/UI';
import { OtpInput } from '../components/OtpInput';
import { AgentNetwork } from '../components/AgentVisual';
import { useStore } from '../store/useStore';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';
import LensVerseLogo from './LenseverseLogo.png';

const API_BASE = import.meta.env.VITE_API_URL || '';

// ─── Pending Approval Banner ──────────────────────────────────────────────────

function PendingApprovalPanel({ requestId }: { requestId?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      className="space-y-6"
    >
      {/* Status orb */}
      <div className="flex justify-center">
        <div className="relative w-20 h-20 bg-amber-500/10 border border-amber-500/20 rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(245,158,11,0.1)]">
          <Clock className="w-10 h-10 text-amber-400" />
          <motion.div
            animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ repeat: Infinity, duration: 2.5 }}
            className="absolute inset-0 rounded-full bg-amber-500/10"
          />
        </div>
      </div>

      <div className="text-center space-y-2">
        <h2 className="text-2xl font-black text-white tracking-tighter italic">
          Access Under Review
        </h2>
        <p className="text-white/40 text-sm font-medium leading-relaxed">
          Your access request is currently being reviewed by our team.
          You will receive an email notification once approved.
        </p>
      </div>

      {requestId && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
            <span className="text-white/20">Request Reference</span>
            <span className="font-mono text-amber-400">{requestId}</span>
          </div>
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
            <span className="text-white/20">Estimated Review</span>
            <span className="text-white/40">24–48 business hours</span>
          </div>
        </div>
      )}

      <p className="text-center text-xs text-white/20 font-medium">
        Questions?{' '}
        <a
          href="mailto:info@vtuae.com"
          className="text-accent hover:underline font-black"
        >
          info@vtuae.com
        </a>
      </p>
    </motion.div>
  );
}

function RejectedPanel() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      className="space-y-6"
    >
      <div className="flex justify-center">
        <div className="w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-full flex items-center justify-center">
          <XCircle className="w-10 h-10 text-red-400" />
        </div>
      </div>
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-black text-white tracking-tighter italic">
          Request Not Approved
        </h2>
        <p className="text-white/40 text-sm font-medium leading-relaxed">
          Your access request was not approved at this time.
        </p>
      </div>
      <p className="text-center text-xs text-white/20 font-medium">
        Contact{' '}
        <a
          href="mailto:info@vtuae.com"
          className="text-accent hover:underline font-black"
        >
          info@vtuae.com
        </a>{' '}
        for assistance.
      </p>
    </motion.div>
  );
}

// ─── Main Login Component ─────────────────────────────────────────────────────

type LoginStep = 'email' | 'otp' | 'pending' | 'rejected';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp } = useStore();
  const { setAuth } = useAuthStore();

  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timer, setTimer] = useState(30);
  const [requestId, setRequestId] = useState<string | undefined>(undefined);

  // Countdown for resend
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (step === 'otp' && timer > 0) {
      interval = setInterval(() => setTimer(t => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // ── Google OAuth ──────────────────────────────────────────────────────────

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async tokenResponse => {
      setIsGoogleLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/auth/google/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: tokenResponse.access_token }),
        });
        const data = await res.json();

        if (res.ok && data.success) {
          setAuth(data.token, data.user);
          toast.success(`Welcome, Agent ${data.user.username}`);
          navigate(data.user.is_superuser ? '/admin' : '/dashboard');
        } else {
          const msg = data.error || 'Google authentication failed.';
          setError(`OAUTH_FAILED: ${msg}`);
          toast.error(msg);
        }
      } catch {
        setError('CONNECTION_ERROR: Network error during Google login.');
        toast.error('Network error during Google login.');
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: err => {
      if (err.error !== 'popup_closed_by_user') {
        toast.error('Google authentication failed.');
        setError(`OAUTH_ERROR: ${err.error_description || 'Unknown Google error'}`);
      }
    },
  });

  // ── OTP send ──────────────────────────────────────────────────────────────

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    setError(null);
    try {
      const success = await sendOtp(email);
      setIsLoading(false);
      if (success) {
        setStep('otp');
        setTimer(30);
        toast('Access Code Sent', {
          description: `Verification code sent to ${email}`,
        });
      } else {
        setError('Failed to send verification code. Please try again.');
      }
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to send verification code.');
    }
  };

  // ── OTP verify ────────────────────────────────────────────────────────────

  const handleVerifyOtp = async (code: string) => {

    setIsLoading(true);
    setError(null);

    try {

      // ✅ use Zustand verifyOtp (uses session_id automatically)
      const user = await verifyOtp(code);

      // ========================
      // LOGIN SUCCESS
      // ========================

      if (user && user.id !== 'pending' && user.id !== 'signup') {

        const token = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('authUser');

        if (token && storedUser) {

          const parsedUser = JSON.parse(storedUser);

          setAuth(token, parsedUser);

          toast.success(`Welcome back, Agent ${parsedUser.username}`);

          if (parsedUser.is_superuser) {
            navigate('/signup-requests');
          } else {
            navigate('/dashboard');
          }

          return;
        }
      }

      // ========================
      // PENDING APPROVAL
      // ========================

      if (user?.id === 'pending') {

        setStep('pending');

        toast('Your account is pending admin approval', { icon: 'ℹ️' });

        return;
      }

      // ========================
      // SIGNUP FLOW
      // ========================

      if (user?.id === 'signup') {

        toast('No approved account found. Please complete signup.', { icon: 'ℹ️' });

        navigate('/auth/signup');

        return;
      }

      // ========================
      // INVALID OTP
      // ========================

      setError('Invalid or expired verification code.');

    } catch (err) {

      console.error('[Login verify error]', err);

      setError('Verification failed. Please try again.');

    } finally {

      setIsLoading(false);
    }
  };




  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex bg-deep-0 overflow-hidden relative">
      {/* Left visual panel */}
      <div className="hidden lg:flex flex-1 relative bg-deep-1 items-center justify-center border-r border-white/5 overflow-hidden">
        <div className="radar-sweep opacity-30" />
        <AgentNetwork />
        <div className="z-10 p-20 max-w-2xl space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-accent/20 blur-2xl rounded-full" />
              <img src={LensVerseLogo} alt="LensVerse" className="relative w-20 h-20 object-contain" />
            </div>

            <h2 className="text-6xl font-black text-white leading-[0.9] tracking-tighter">
              Access <br />
              <span className="italic text-accent">Lenseverse Workspace.</span>
            </h2>

            <p className="text-white/40 text-xl font-medium leading-relaxed max-w-lg">
              Seamlessly manage your enterprise identity.
            </p>

            <div className="grid grid-cols-2 gap-8 pt-6">
              {[
                { icon: Shield, label: 'Identity Secure', val: 'End-to-End' },
                { icon: Fingerprint, label: 'Access', val: 'OTP Auth' },
              ].map((item, i) => (
                <div key={i} className="space-y-2 group">
                  <item.icon className="w-8 h-8 text-accent/40 group-hover:text-accent transition-colors" />
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/20">{item.label}</p>
                  <p className="text-sm font-bold text-white/80">{item.val}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-10 flex gap-4 text-[10px] font-bold text-white/10 uppercase tracking-[0.5em]">
          <span className="flex items-center gap-2">
            <div className="w-1 h-1 bg-green-500 rounded-full" /> SYSTEM_ONLINE
          </span>
          <span className="flex items-center gap-2">
            <div className="w-1 h-1 bg-green-500 rounded-full" /> ENCRYPTION_ACTIVE
          </span>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-8 md:p-20 relative bg-deep-0">
        <Link
          to="/"
          className="absolute top-6 right-6 sm:top-10 sm:right-10 text-white/50 hover:text-white transition-colors flex items-center gap-2 text-[12px] font-black uppercase tracking-widest"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">

            {/* ── Email step ────────────────────────────────────────────── */}
            {step === 'email' && (
              <motion.div
                key="email"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-12"
              >
                <div className="space-y-3">
                  <h1 className="text-5xl font-black text-white tracking-tighter">Sign In</h1>
                  <p className="text-white/40 font-medium">Enter your authorized work email.</p>
                </div>

                <div className="space-y-6">
                  <div className="flex flex-col gap-4">
                    <Button
                      variant="glass"
                      className="h-14 w-full font-black text-[11px] uppercase tracking-widest"
                      onClick={() => handleGoogleLogin()}
                      disabled={isGoogleLoading}
                    >
                      {isGoogleLoading ? (
                        <><Loader2 className="w-4 h-4 mr-3 animate-spin" />Connecting…</>
                      ) : (
                        <><img src="https://www.google.com/favicon.ico" className="w-4 h-4 mr-3" alt="" />Continue with Google</>
                      )}
                    </Button>

                  </div>

                  <div className="relative py-4 flex items-center gap-4">
                    <div className="h-px flex-1 bg-white/5" />
                    <span className="text-[12px] font-black uppercase tracking-[0.3em] text-white/50 whitespace-nowrap">
                      Or use your email
                    </span>
                    <div className="h-px flex-1 bg-white/5" />
                  </div>

                  <form onSubmit={handleSendOtp} className="space-y-6">
                    <Input
                      label="Work Email Address"
                      placeholder="name@company.com"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                    />
                    <Button isLoading={isLoading} className="w-full" size="xl">
                      Continue <ArrowRight className="ml-3 w-4 h-4" />
                    </Button>
                  </form>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 p-5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl text-[11px] font-mono"
                    >
                      <AlertCircle className="w-5 h-5 shrink-0" />
                      {error}
                    </motion.div>
                  )}
                </div>

                <div className="text-center space-y-4">
                  <p className="text-sm text-white/40 font-medium pb-8">
                    Don't have an account?{' '}
                    <Link to="/auth/signup" className="text-accent font-black hover:underline">
                      Request access
                    </Link>
                  </p>
                </div>
              </motion.div>
            )}

            {/* ── OTP step ──────────────────────────────────────────────── */}
            {step === 'otp' && (
              <motion.div
                key="otp"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-12"
              >
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center text-accent ring-1 ring-accent/20">
                    <Zap className="w-8 h-8" />
                  </div>
                  <div className="space-y-2">
                    <h1 className="text-4xl font-black text-white tracking-tighter">
                      Check your email
                    </h1>
                    <p className="text-white/40 leading-relaxed font-medium">
                      We sent a verification code to <span className="text-white italic">{email}</span>.
                    </p>
                  </div>
                </div>

                <div className="space-y-10">
                  <OtpInput onComplete={handleVerifyOtp} />

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 p-5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl text-[11px] font-mono"
                    >
                      <AlertCircle className="w-5 h-5 shrink-0" />
                      {error}
                    </motion.div>
                  )}

                  <div className="space-y-8">
                    <div className="text-center">
                      <p className="text-xs font-black uppercase tracking-widest text-white/30">
                        Signal strength: stable ·{' '}
                        {timer > 0 ? (
                          <span className="text-accent">Retry in {timer}s</span>
                        ) : (
                          <button
                            onClick={() => { sendOtp(email); setTimer(30); }}
                            className="text-accent hover:underline"
                          >
                            Request New Token
                          </button>
                        )}
                      </p>
                    </div>

                    <button
                      onClick={() => { setStep('email'); setError(null); }}
                      className="w-full text-[10px] font-black text-white/20 uppercase tracking-[0.4em] hover:text-white transition-all flex items-center justify-center gap-3"
                    >
                      <ArrowLeft className="w-3 h-3" /> Use a different email
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Pending approval ──────────────────────────────────────── */}
            {step === 'pending' && (
              <motion.div
                key="pending"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <PendingApprovalPanel requestId={requestId} />
                <button
                  onClick={() => { setStep('email'); setError(null); setEmail(''); }}
                  className="w-full text-[10px] font-black text-white/20 uppercase tracking-[0.4em] hover:text-white transition-all flex items-center justify-center gap-3"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to Login
                </button>
              </motion.div>
            )}

            {/* ── Rejected ──────────────────────────────────────────────── */}
            {step === 'rejected' && (
              <motion.div
                key="rejected"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <RejectedPanel />
                <button
                  onClick={() => { setStep('email'); setError(null); setEmail(''); }}
                  className="w-full text-[10px] font-black text-white/20 uppercase tracking-[0.4em] hover:text-white transition-all flex items-center justify-center gap-3"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to Login
                </button>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Login;